import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { getStockTime } from '@src/services/api';
import { getJumpUrlHost } from '@src/utils/index';
import * as echarts from 'echarts';

const Column = ({ title, marketVarieties, allMarket }) => {
    const [stockIndex, setStockIndex] = useState(0);
    const [stockTime, setStockTime] = useState({});
    let marketData = marketVarieties?.[title] || [];
    const filterStockNames = ['英国富时100指数', '法国CAC40指数', '马来西亚富时综合指数', '新华富时中国A50指数'];

    marketData = marketData.filter(item => !filterStockNames.includes(item.name));
    const marketQuoteCodes = marketData.slice(0, 5).map(item => item.quoteCode);

    const stockList = allMarket.filter(item => marketQuoteCodes.includes(item.quoteCode));
    const canvasRef = useRef(null);

    // stockList按照marketQuoteCodes中quoteCode值进行排序
    stockList.sort((a, b) => {
        const indexA = marketQuoteCodes.indexOf(a.quoteCode);
        const indexB = marketQuoteCodes.indexOf(b.quoteCode);

        return indexA - indexB;
    });

    const handleMouseEnter = index => {
        setStockIndex(index);
    };

    const creatArrayList = (allTimeDot, addDotNum) => {
        const dataList = allTimeDot.map(item => item?.tm || '');

        for (let i = 0; i < addDotNum; i++) {
            dataList.push('');
        }

        return dataList;
    };

    const canvasHandle = (allTimeDot, echartsInfo) => {
        const echartsType = echartsInfo?.xAxis_type || 'time';
        const myChart = echarts.init(canvasRef.current);
        // 计算数据列表allTimeDot最小值和最大值
        const echartsData = allTimeDot.map(item => [item.tm, item.close]);
        const addDotNum = echartsInfo?.lackNum || 0;
        const timeEmptyList = creatArrayList(allTimeDot, addDotNum);
        const optionxAxisData = echartsType === 'time' ? echartsData : timeEmptyList;

        // console.log(echartsData);
        const option = {
            xAxis: {
                type: echartsType,
                data: optionxAxisData,
                boundaryGap: false,
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    show: false,
                },
                max: echartsInfo?.startAndEndTime?.fullEndTime,
            },
            yAxis: {
                type: 'value',
                splitLine: {
                    show: true,
                },
                show: true,
                scale: true,
            },
            series: [
                {
                    type: 'line',
                    showSymbol: false,
                    smooth: true,
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                {
                                    offset: 0,
                                    color: '#608AE3', // 0% 处的颜色
                                },
                                {
                                    offset: 1,
                                    color: '#ffffff', // 100% 处的颜色
                                },
                            ],
                            global: false, // 缺省为 false
                        },
                    },
                    data: echartsData,
                    lineStyle: {
                        color: '#608AE3',
                        width: 0.5,
                    },
                },
            ],
            tooltip: {
                trigger: 'axis',
            },
            graphic: [
                {
                    type: 'image',
                    id: 'logo',
                    right: 45,
                    bottom: 65,
                    style: {
                        image:
                            'https://x0.ifengimg.com/ucms/2024_10/8827E81EBDC5F313B1386D0E722D6CB0A2A5F22E_size2_w148_h43.png', // 水印图片的 URL
                        width: 148,
                        height: 43,
                        opacity: 1,
                    },
                },
            ],
        };

        myChart.setOption(option);
    };

    const getStockTimeHandle = async () => {
        try {
            const liveQuoteCode = stockList[stockIndex].quoteCode;
            const stockTime = await getStockTime(liveQuoteCode);
            const allTimeDot = stockTime?.quoteTSDList || [];
            const echartInfo = stockTime?.echarts || {};

            setStockTime({
                startTime: echartInfo?.startAndEndTime?.startTime || '',
                endTime: echartInfo?.startAndEndTime?.endTime || '',
            });
            canvasHandle(allTimeDot, stockTime?.echarts);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(
        () => {
            getStockTimeHandle();
            const timer = setInterval(() => {
                getStockTimeHandle();
            }, 60 * 1000);

            return () => {
                clearInterval(timer);
            };
        },
        [stockIndex],
    );

    return (
        <div className={styles.column}>
            <div className={styles.columnHd}>
                <span className={styles.columnHdName}>{title}</span>
                <a
                    href={`${getJumpUrlHost('pc')}/marketCenter?navName=${encodeURIComponent(title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.columnHdMore}>
                    更多
                    <img
                        className={styles.columnHdMorePic}
                        src="https://x0.ifengimg.com/ucms/2023_51/1B31222AEB9B5A3188B4C1B9F96A08D5A224EBB8_size1_w28_h28.png"
                        width="14"
                        height="14"
                    />
                </a>
            </div>
            <div className={styles.columnBd}>
                <div className={styles.columnBdCanvasBox}>
                    <div className={styles.columnBdCanvas} ref={canvasRef} />
                    <div className={styles.columnBdStartTime}>{stockTime?.startTime || ''}</div>
                    <div className={styles.columnBdEndTime}>{stockTime?.endTime || ''}</div>
                </div>
                <div className={styles.columnBdBox}>
                    <table className={styles.columnBdTable}>
                        <tbody>
                            {stockList.map((item, index) => {
                                let statusFlag = item.rangePercent.includes('-') ? 'down' : 'up';
                                const statusClassList = {
                                    down: styles.columnBdTableGreen,
                                    up: styles.columnBdTableRed,
                                    flat: styles.columnBdTableGray,
                                };

                                statusFlag = item.rangePercent.includes('--') ? 'flat' : statusFlag;

                                return (
                                    <tr
                                        key={index}
                                        onMouseEnter={() => {
                                            handleMouseEnter(index);
                                        }}
                                        className={`${styles.columnBdTableTr} ${
                                            stockIndex === index ? styles.columnBdTableActive : ''
                                        }`}>
                                        <td className={styles.columnBdTableTd}>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`${getJumpUrlHost(
                                                    'pc',
                                                )}/marketDetail?quoteCode=${encodeURIComponent(item.quoteCode)}`}>
                                                {item.name}
                                            </a>
                                        </td>
                                        <td className={styles.columnBdTableTd}>{item.tradePrice}</td>
                                        <td className={styles.columnBdTableTd}>
                                            <span className={statusClassList[statusFlag]}>
                                                {statusFlag === 'up' ? `+${item.range}` : item.range}
                                            </span>
                                        </td>
                                        <td className={styles.columnBdTableTd}>
                                            <span className={statusClassList[statusFlag]}>
                                                {statusFlag === 'up' ? `+${item.rangePercent}` : item.rangePercent}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

Column.propTypes = {
    allMarket: PropTypes.array,
    title: PropTypes.string,
    marketVarieties: PropTypes.object,
};

export default errorBoundary(Column);
